import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { AlertSeverity, PageLayout, SettingDefaults, SettingValue, SourceConnection } from '@/types'
import * as api from '@/lib/api'
import type { ViewTableState } from '@/lib/view'

const PUT_DELAY_MS = 500

export const useSettingsStore = defineStore('settings', () => {
  const me = ref<SettingValue>({})
  const loaded = ref(false)
  let putTimer: number | null = null

  const getMeSettings = async (): Promise<SettingValue> => {
    const setting = await api.getMeSettings()
    me.value = setting.value ?? {}
    loaded.value = true
    return me.value
  }

  const putMeSettings = (patch: SettingValue) => {
    me.value = { ...me.value, ...patch }
    if (putTimer != null) window.clearTimeout(putTimer)
    putTimer = window.setTimeout(() => {
      putTimer = null
      void api.putMeSettings(me.value).catch(() => undefined)
    }, PUT_DELAY_MS)
  }

  const tables = computed(() => (me.value.tables ?? {}) as Record<string, ViewTableState>)

  const getTableState = (key: string): ViewTableState | undefined => tables.value[key]

  const putTableState = (key: string, state: ViewTableState) => putMeSettings({ tables: { ...tables.value, [key]: state } })

  const deleteTableStates = (prefix: string) =>
    putMeSettings({ tables: Object.fromEntries(Object.entries(tables.value).filter(([key]) => !key.startsWith(`${prefix}:`))) })

  const hasTableStates = (prefix: string) => Object.keys(tables.value).some((key) => key.startsWith(`${prefix}:`))

  const putMeDefaults = (patch: SettingDefaults) => {
    putMeSettings({ defaults: { ...me.value.defaults, ...patch } })
  }

  const sourceDefaults = computed<SourceConnection>(() => ({
    poll_ms: me.value.defaults?.source?.poll_ms ?? 5000,
    max_entries: me.value.defaults?.source?.max_entries ?? 500,
    trailing_window: me.value.defaults?.source?.trailing_window ?? '15m',
  }))

  const pageDefaults = computed<PageLayout>(() => ({
    mode: me.value.defaults?.page?.mode ?? 'dynamic',
    columns: me.value.defaults?.page?.columns ?? 12,
    rows: me.value.defaults?.page?.rows ?? 12,
    gap: me.value.defaults?.page?.gap ?? 'roomy',
    scroll_x: me.value.defaults?.page?.scroll_x ?? false,
  }))


  const placementDefaults = computed(() => ({
    w: me.value.defaults?.placement?.w ?? 6,
    h: me.value.defaults?.placement?.h ?? 8,
    annotations: me.value.defaults?.placement?.annotations ?? true,
  }))

  const alertDefaults = computed<{ interval_seconds: number; severity: AlertSeverity }>(() => ({
    interval_seconds: me.value.defaults?.alert?.interval_seconds ?? 60,
    severity: me.value.defaults?.alert?.severity ?? 'warning',
  }))


  return { me, loaded, getMeSettings, putMeSettings, putMeDefaults, getTableState, putTableState, deleteTableStates, hasTableStates, sourceDefaults, pageDefaults, placementDefaults, alertDefaults }
})
